export interface KeywordData {
  primary: string[];
  secondary: string[];
  longTail: string[];
  local: string[];
  conversion: string[];
}

export const globalKeywords: KeywordData = {
  primary: [
    'psychotherapist',
    'psychologist',
    'psychiatrist',
    'mental health professional',
    'therapist',
    'counselor'
  ],
  secondary: [
    'therapy',
    'counseling',
    'mental health',
    'psychological services',
    'behavioral therapy',
    'cognitive therapy',
    'psychotherapy sessions'
  ],
  longTail: [
    'licensed mental health professional',
    'certified psychotherapist',
    'experienced psychologist',
    'qualified mental health counselor',
    'professional therapy services',
    'mental health treatment specialist'
  ],
  local: [
    'near me',
    'in my area',
    'local',
    'nearby',
    'close to me'
  ],
  conversion: [
    'book appointment',
    'schedule session',
    'find therapist',
    'get help',
    'start therapy',
    'professional help'
  ]
};

export const specialtyKeywords: KeywordData = {
  primary: [
    'anxiety therapy',
    'depression treatment',
    'couples therapy',
    'family counseling',
    'trauma therapy',
    'addiction counseling'
  ],
  secondary: [
    'cognitive behavioral therapy',
    'psychodynamic therapy',
    'humanistic therapy',
    'gestalt therapy',
    'acceptance commitment therapy',
    'dialectical behavior therapy'
  ],
  longTail: [
    'anxiety and panic disorder treatment',
    'depression and mood disorder therapy',
    'relationship and couples counseling',
    'family conflict resolution therapy',
    'trauma and PTSD treatment',
    'substance abuse and addiction recovery'
  ],
  local: [
    'anxiety therapist near me',
    'depression counselor in',
    'couples therapy near',
    'family therapist in',
    'trauma specialist near',
    'addiction counselor in'
  ],
  conversion: [
    'get anxiety help now',
    'start depression treatment',
    'book couples session',
    'find family therapist',
    'trauma therapy appointment',
    'addiction help available'
  ]
};

export const pageTypeKeywords = {
  home: {
    title: 'Find Therapists, Psychologists & Psychotherapists Worldwide | Global Directory',
    h1: 'Professional Therapists, Psychologists & Psychotherapists Worldwide',
    keywords: [
      'therapists worldwide',
      'psychologists global',
      'psychotherapists international',
      'find therapist online',
      'search psychologist',
      'locate psychotherapist',
      'licensed therapists',
      'certified psychologists',
      'professional psychotherapists',
      'mental health professionals',
      'worldwide therapy',
      'global mental health',
      'international therapists',
      'online therapy platform',
      'verified mental health professionals',
      'therapist directory',
      'psychologist finder',
      'psychotherapist search'
    ],
    description: 'Find qualified therapists, psychologists & psychotherapists worldwide. Connect with licensed mental health professionals globally. Professional therapy, psychology, and psychotherapy services through secure online and in-person sessions with verified specialists.'
  },
  
  city: (cityName: string, countryName: string) => ({
    title: `Therapists, Psychologists & Psychotherapists in ${cityName} | Licensed Mental Health`,
    h1: `Expert Therapists, Psychologists & Psychotherapists in ${cityName}`,
    keywords: [
      `therapists in ${cityName}`,
      `psychologists in ${cityName}`,
      `psychotherapists in ${cityName}`,
      `therapist ${cityName}`,
      `psychologist ${cityName}`,
      `psychotherapist ${cityName}`,
      `licensed therapists ${cityName}`,
      `certified psychologists ${cityName}`,
      `professional psychotherapists ${cityName}`,
      `mental health professionals ${cityName}`,
      `therapy ${cityName}`,
      `counseling ${cityName}`,
      `psychological services ${cityName}`,
      `${cityName} therapists`,
      `${cityName} psychologists`,
      `${cityName} psychotherapists`,
      `best therapists ${cityName}`,
      `experienced psychologists ${cityName}`,
      `qualified psychotherapists ${cityName}`,
      `online therapists ${cityName}`,
      `anxiety therapists ${cityName}`,
      `depression psychologists ${cityName}`,
      `couples psychotherapists ${cityName}`
    ],
    description: `Find qualified therapists, psychologists & psychotherapists in ${cityName}, ${countryName}. Expert mental health professionals offering therapy, counseling, and psychological services. Connect with licensed therapists, experienced psychologists, and certified psychotherapists for online and in-person sessions.`
  }),
  
  country: (countryName: string) => ({
    title: `Therapists, Psychologists & Psychotherapists in ${countryName} | Mental Health Directory`,
    h1: `Professional Therapists, Psychologists & Psychotherapists in ${countryName}`,
    keywords: [
      `therapists in ${countryName}`,
      `psychologists in ${countryName}`,
      `psychotherapists in ${countryName}`,
      `licensed therapists ${countryName}`,
      `certified psychologists ${countryName}`,
      `professional psychotherapists ${countryName}`,
      `mental health professionals ${countryName}`,
      `therapy in ${countryName}`,
      `counseling services ${countryName}`,
      `psychological services ${countryName}`,
      `${countryName} therapists`,
      `${countryName} psychologists`,
      `${countryName} psychotherapists`,
      `best therapists ${countryName}`,
      `experienced psychologists ${countryName}`,
      `qualified psychotherapists ${countryName}`,
      `online therapists ${countryName}`,
      `mental health treatment ${countryName}`,
      `therapy sessions ${countryName}`,
      `behavioral therapists ${countryName}`,
      `cognitive psychologists ${countryName}`
    ],
    description: `Discover qualified therapists, psychologists & psychotherapists in ${countryName}. Connect with licensed mental health professionals offering specialized therapy services. Find experienced therapists, certified psychologists, and professional psychotherapists for online and in-person sessions throughout ${countryName}.`
  }),
  
  specialties: {
    title: 'Therapy Specialties | Expert Mental Health Treatment Services',
    h1: 'Specialized Mental Health Treatment Services',
    keywords: [
      ...specialtyKeywords.primary,
      ...specialtyKeywords.secondary,
      'therapy specializations',
      'mental health specialties',
      'treatment approaches',
      'therapeutic methods',
      'specialized counseling'
    ],
    description: 'Explore comprehensive therapy specialties and treatment approaches. Find experts in anxiety therapy, depression treatment, couples counseling, trauma therapy, addiction recovery, and specialized therapeutic methods tailored to your mental health needs.'
  },
  
  search: {
    title: 'Find Your Perfect Therapist | Search Mental Health Professionals',
    h1: 'Find the Right Mental Health Professional for You',
    keywords: [
      'find therapist',
      'search psychologist',
      'locate mental health professional',
      'therapy search',
      'counselor finder',
      'therapist directory',
      'mental health search',
      'find counselor',
      'therapist locator',
      'psychology search'
    ],
    description: 'Search and find the perfect mental health professional for your needs. Filter by location, specialty, approach, and availability. Connect with licensed therapists, psychologists, and counselors who match your specific requirements.'
  },
  
  therapists: {
    title: 'For Mental Health Professionals | Join Our Global Network',
    h1: 'Expand Your Practice with Our Global Platform',
    keywords: [
      'therapist platform',
      'psychologist network',
      'mental health professional platform',
      'therapy practice growth',
      'online therapy platform',
      'therapist directory listing',
      'expand therapy practice',
      'mental health professional network'
    ],
    description: 'Join our global network of mental health professionals. Expand your practice, connect with clients worldwide, and grow your therapy business with our secure platform designed for licensed therapists, psychologists, and counselors.'
  }
};

export const generateSEOKeywords = (pageType: string, entityData?: any): string[] => {
  const baseKeywords = globalKeywords.primary.concat(globalKeywords.secondary);
  
  switch (pageType) {
    case 'city':
      if (entityData?.city) {
        return pageTypeKeywords.city(entityData.city.name, entityData.city.country).keywords;
      }
      break;
    case 'country':
      if (entityData?.country) {
        return pageTypeKeywords.country(entityData.country.name).keywords;
      }
      break;
    case 'specialties':
      return pageTypeKeywords.specialties.keywords;
    case 'search':
      return pageTypeKeywords.search.keywords;
    case 'therapists':
      return pageTypeKeywords.therapists.keywords;
    default:
      return pageTypeKeywords.home.keywords;
  }
  
  return baseKeywords;
};

export const generateMetaDescription = (pageType: string, entityData?: any): string => {
  switch (pageType) {
    case 'city':
      if (entityData?.city) {
        return pageTypeKeywords.city(entityData.city.name, entityData.city.country).description;
      }
      break;
    case 'country':
      if (entityData?.country) {
        return pageTypeKeywords.country(entityData.country.name).description;
      }
      break;
    case 'specialties':
      return pageTypeKeywords.specialties.description;
    case 'search':
      return pageTypeKeywords.search.description;
    case 'therapists':
      return pageTypeKeywords.therapists.description;
    default:
      return pageTypeKeywords.home.description;
  }
  
  return pageTypeKeywords.home.description;
};

export const generatePageTitle = (pageType: string, entityData?: any): string => {
  switch (pageType) {
    case 'city':
      if (entityData?.city) {
        return pageTypeKeywords.city(entityData.city.name, entityData.city.country).title;
      }
      break;
    case 'country':
      if (entityData?.country) {
        return pageTypeKeywords.country(entityData.country.name).title;
      }
      break;
    case 'specialties':
      return pageTypeKeywords.specialties.title;
    case 'search':
      return pageTypeKeywords.search.title;
    case 'therapists':
      return pageTypeKeywords.therapists.title;
    default:
      return pageTypeKeywords.home.title;
  }
  
  return pageTypeKeywords.home.title;
};

export const generateH1Title = (pageType: string, entityData?: any): string => {
  switch (pageType) {
    case 'city':
      if (entityData?.city) {
        return pageTypeKeywords.city(entityData.city.name, entityData.city.country).h1;
      }
      break;
    case 'country':
      if (entityData?.country) {
        return pageTypeKeywords.country(entityData.country.name).h1;
      }
      break;
    case 'specialties':
      return pageTypeKeywords.specialties.h1;
    case 'search':
      return pageTypeKeywords.search.h1;
    case 'therapists':
      return pageTypeKeywords.therapists.h1;
    default:
      return pageTypeKeywords.home.h1;
  }
  
  return pageTypeKeywords.home.h1;
};